/**
 * GrooveMail contact/lead CSV exports return streamed text/csv
 * (GET /maillist?exportContact= and GET /download-lead-csv).
 *
 * Root cause of a file whose only cell is the word "undefined":
 * axios 0.19 leaves a populated non-JSON body as a raw string. getData
 * returns the axios response without unwrapping `.data`, and the GrooveMail
 * interceptor returns `response` unchanged. MailListContactCsv headings
 * start with "Name"; PHP never writes the word "undefined".
 * `new Blob([response.data])` writes that word only when `response.data`
 * is unset — an empty/missing body (MailLeadsController::downloadLeadCsv
 * can return nothing; streamDownload + Content-Disposition can leave XHR
 * responseText empty). Request the stream as a blob, refuse empty/JSON
 * bodies, and throw so the existing error alert shows.
 */

export const CSV_EXPORT_RESPONSE_TYPE = 'blob';
export const CSV_MIME_TYPE = 'text/csv;charset=utf-8';
export const CSV_EXPORT_EMPTY_ERROR = 'CSV export returned no data';

export function csvExportRequestConfig(extraConfig) {
  return {
    ...(extraConfig || {}),
    responseType: CSV_EXPORT_RESPONSE_TYPE,
  };
}

export function axios19JsonTransform(data) {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      return data;
    }
  }
  return data;
}

export function parseCsvExportJson(text) {
  const trimmed = String(text == null ? '' : text).replace(/^\uFEFF/, '').trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return null;
  }
}

export function csvExportErrorFromJson(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return payload.error || payload.message || payload.msg || null;
}

function csvExportError(message, payload) {
  const error = new Error(message);
  error.response = {
    data: payload && typeof payload === 'object'
      ? { ...payload, error: payload.error || message }
      : { error: message },
  };
  return error;
}

export async function readExportResponseText(data) {
  if (data == null) {
    return '';
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return data.text();
  }
  if (typeof data === 'string') {
    return data;
  }
  return '';
}

export async function csvBlobFromResponseData(data, requiredHeaders) {
  if (data && typeof data === 'object' && typeof Blob !== 'undefined' && !(data instanceof Blob)) {
    throw csvExportError(
      csvExportErrorFromJson(data) || 'CSV export returned JSON instead of text/csv',
      data
    );
  }

  const text = await readExportResponseText(data);
  if (!String(text).replace(/^\uFEFF/, '').trim()) {
    throw csvExportError(CSV_EXPORT_EMPTY_ERROR);
  }

  const trimmed = String(text).replace(/^\uFEFF/, '').trim();
  const json = parseCsvExportJson(text);
  if (json !== null || trimmed === 'null') {
    throw csvExportError(
      csvExportErrorFromJson(json) || 'CSV export returned JSON instead of text/csv',
      typeof json === 'object' ? json : { error: 'CSV export returned JSON instead of text/csv' }
    );
  }

  if (trimmed === 'undefined' || /^<(?:!doctype|html|head|body|\?xml|error)\b/i.test(trimmed)) {
    throw csvExportError('CSV export returned an invalid file');
  }

  if (requiredHeaders && requiredHeaders.length) {
    const headers = trimmed.split(/\r?\n/, 1)[0].split(',').map(header => header.trim().replace(/^"|"$/g, ''));
    if (!requiredHeaders.every((header, index) => headers[index] === header)) {
      throw csvExportError('CSV export is missing the expected contact columns');
    }
  }

  // Blob.text() strips a UTF-8 BOM. Preserve the original bytes for Excel.
  return typeof Blob !== 'undefined' && data instanceof Blob
    ? data.slice(0, data.size, CSV_MIME_TYPE)
    : new Blob([text], { type: CSV_MIME_TYPE });
}

export async function normalizeCsvExportError(error) {
  const data = error && error.response ? error.response.data : null;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
    return error;
  }

  const text = await data.text();
  const json = parseCsvExportJson(text);
  const message = csvExportErrorFromJson(json) || text || error.message || 'CSV export failed';
  const normalized = error instanceof Error ? error : new Error(message);
  normalized.message = message;
  normalized.response = {
    ...(error.response || {}),
    data: json && typeof json === 'object' ? { ...json, error: json.error || message } : { error: message },
  };
  return normalized;
}

export async function triggerCsvDownload(data, fileName, requiredHeaders) {
  const csvBlob = await csvBlobFromResponseData(data, requiredHeaders);
  const fileURL = window.URL.createObjectURL(csvBlob);
  const fileLink = document.createElement('a');
  fileLink.href = fileURL;
  fileLink.setAttribute('download', fileName);
  try {
    document.body.appendChild(fileLink);
    fileLink.click();
  } finally {
    fileLink.remove();
    setTimeout(() => window.URL.revokeObjectURL(fileURL), 0);
  }
  return csvBlob;
}
